import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoSelector from "@/components/AnnoSelector";

const SETTORI = ["PRIMARIA", "SECONDARIA"];
const CURRENT_YEAR = new Date().getFullYear();

const fmtEuro = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const empty = () => ({
  anno: CURRENT_YEAR,
  settore: "PRIMARIA",
  iscrizione: 0,
  rate: 0,
  retta: 0,
  mensa_listino: 0,
  mensa_agevolata: 0,
  settimane_scuola_anno: 0,
});

export default function ScuolaListinoPage() {
  const { user, annoCorrente } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // id riga in modifica o "new"
  const [draft, setDraft] = useState(empty());
  const [filterAnno, setFilterAnno] = useState("");
  const [filterSett, setFilterSett] = useState("");
  const [saving, setSaving] = useState(false);

  // Sync con anno globale segremat: cambio dal selettore in alto → filtra subito
  useEffect(() => {
    if (annoCorrente) setFilterAnno(String(annoCorrente));
  }, [annoCorrente]);

  const carica = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/scuola-listino/");
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento listino");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carica(); }, []);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filterAnno && Number(it.anno) !== Number(filterAnno)) return false;
      if (filterSett && it.settore !== filterSett) return false;
      return true;
    });
  }, [items, filterAnno, filterSett]);

  const startEdit = (it) => {
    setEditing(it.id);
    setDraft({ ...it });
  };

  const startNew = () => {
    setEditing("new");
    setDraft(empty());
  };

  const cancel = () => {
    setEditing(null);
    setDraft(empty());
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        anno: Number(draft.anno) || CURRENT_YEAR,
        settore: draft.settore,
        iscrizione: Number(draft.iscrizione) || 0,
        rate: Number(draft.rate) || 0,
        retta: Number(draft.retta) || 0,
        mensa_listino: Number(draft.mensa_listino) || 0,
        mensa_agevolata: Number(draft.mensa_agevolata) || 0,
        settimane_scuola_anno: Number(draft.settimane_scuola_anno) || 0,
      };
      if (editing === "new") {
        await api.post("/scuola-listino/", payload);
      } else {
        await api.put(`/scuola-listino/${editing}`, payload);
      }
      await carica();
      cancel();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare il listino ${it.anno} · ${it.settore}?`)) return;
    try {
      await api.delete(`/scuola-listino/${it.id}`);
      await carica();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div style={{ padding: 16 }} data-testid="scuola-listino-page">
      <div style={{ background: "#f5f5f5", padding: 10, marginBottom: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: 4 }}>
        <AnnoSelector testId="listino-anno-attivo" />
        <input
          type="number"
          placeholder="Filtra per anno (override)"
          value={filterAnno}
          onChange={(e) => setFilterAnno(e.target.value)}
          data-testid="listino-filter-anno"
          style={{ width: 170, padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }}
        />
        <select value={filterSett} onChange={(e) => setFilterSett(e.target.value)}
                data-testid="listino-filter-settore"
                style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
          <option value="">Tutti i settori</option>
          {SETTORI.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div style={{ fontSize: 11, color: "#555" }}>
          <b>{filtered.length}</b> riga{filtered.length === 1 ? "" : "he"}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={carica} disabled={loading} data-testid="listino-refresh"
                  style={{ padding: "6px 12px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            {loading ? "…" : "🔄 Aggiorna"}
          </button>
          {isAdmin && editing !== "new" && (
            <button onClick={startNew} data-testid="listino-new"
                    style={{ padding: "6px 14px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              ➕ Nuova riga
            </button>
          )}
        </div>
      </div>

      {err && (
        <div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, marginBottom: 8, fontSize: 12, borderRadius: 3 }}>
          {err}
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", background: "white", minWidth: 920 }}>
          <thead>
            <tr style={{ background: "#1a237e", color: "white" }}>
              <th style={th}>ANNO</th>
              <th style={th}>SETTORE</th>
              <th style={thR}>ISCRIZIONE €</th>
              <th style={thR}>N° RATE</th>
              <th style={thR}>RETTA €</th>
              <th style={thR}>MENSA LISTINO €</th>
              <th style={thR}>MENSA AGEVOLATA €</th>
              <th style={thR}>SETT. SCUOLA</th>
              <th style={{ ...th, width: 220, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {editing === "new" && (
              <EditRow draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} />
            )}
            {loading && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento…</td></tr>
            )}
            {!loading && filtered.length === 0 && editing !== "new" && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#888", fontStyle: "italic" }}>
                Nessun listino. {isAdmin ? "Clicca \"Nuova riga\" per aggiungere il primo." : ""}
              </td></tr>
            )}
            {!loading && filtered.map((it, i) => {
              const isEdit = editing === it.id;
              if (isEdit) {
                return <EditRow key={it.id} draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} />;
              }
              return (
                <tr key={it.id} data-testid={`listino-row-${it.id}`}
                    style={{ borderTop: "1px solid #eee", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                  <td style={td}>{it.anno}/{String((it.anno || 0) + 1).slice(-2)}</td>
                  <td style={{ ...td, fontWeight: 700, color: it.settore === "PRIMARIA" ? "#1565c0" : "#6a1b9a" }}>
                    {it.settore}
                  </td>
                  <td style={tdR}>€ {fmtEuro(it.iscrizione)}</td>
                  <td style={tdR}>{it.rate}</td>
                  <td style={tdR}>€ {fmtEuro(it.retta)}</td>
                  <td style={tdR}>€ {fmtEuro(it.mensa_listino)}</td>
                  <td style={tdR}>€ {fmtEuro(it.mensa_agevolata)}</td>
                  <td style={tdR}>{it.settimane_scuola_anno || 0}</td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {isAdmin ? (
                      <div style={{ display: "inline-flex", gap: 4 }}>
                        <button onClick={() => startEdit(it)} data-testid={`listino-edit-${it.id}`}
                                title="Modifica" style={btnSm("#1565c0")}>✏ Modifica</button>
                        <button onClick={() => remove(it)} data-testid={`listino-del-${it.id}`}
                                title="Elimina" style={btnSm("#c62828")}>🗑</button>
                      </div>
                    ) : (
                      <span style={{ color: "#aaa", fontSize: 10 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditRow({ draft, setDraft, onSave, onCancel, saving }) {
  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <tr style={{ background: "#fff8e1", borderTop: "2px solid #ffb300" }} data-testid="listino-edit-row">
      <td style={td}>
        <input type="number" value={draft.anno} onChange={(e) => upd("anno", e.target.value)}
               data-testid="listino-input-anno"
               style={inpEdit} />
      </td>
      <td style={td}>
        <select value={draft.settore} onChange={(e) => upd("settore", e.target.value)}
                data-testid="listino-input-settore"
                style={inpEdit}>
          {SETTORI.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={td}>
        <input type="number" step="0.01" value={draft.iscrizione} onChange={(e) => upd("iscrizione", e.target.value)}
               data-testid="listino-input-iscrizione"
               style={{ ...inpEdit, textAlign: "right" }} />
      </td>
      <td style={td}>
        <input type="number" value={draft.rate} onChange={(e) => upd("rate", e.target.value)}
               data-testid="listino-input-rate"
               style={{ ...inpEdit, textAlign: "right" }} />
      </td>
      <td style={td}>
        <input type="number" step="0.01" value={draft.retta} onChange={(e) => upd("retta", e.target.value)}
               data-testid="listino-input-retta"
               style={{ ...inpEdit, textAlign: "right" }} />
      </td>
      <td style={td}>
        <input type="number" step="0.01" value={draft.mensa_listino} onChange={(e) => upd("mensa_listino", e.target.value)}
               data-testid="listino-input-mensa-listino"
               style={{ ...inpEdit, textAlign: "right" }} />
      </td>
      <td style={td}>
        <input type="number" step="0.01" value={draft.mensa_agevolata} onChange={(e) => upd("mensa_agevolata", e.target.value)}
               data-testid="listino-input-mensa-agevolata"
               style={{ ...inpEdit, textAlign: "right" }} />
      </td>
      <td style={td}>
        <input type="number" value={draft.settimane_scuola_anno} onChange={(e) => upd("settimane_scuola_anno", e.target.value)}
               data-testid="listino-input-settimane"
               style={{ ...inpEdit, textAlign: "right" }} />
      </td>
      <td style={{ ...td, textAlign: "center" }}>
        <div style={{ display: "inline-flex", gap: 4 }}>
          <button onClick={onSave} disabled={saving} data-testid="listino-save"
                  style={btnSm("#2e7d32")}>{saving ? "…" : "💾 Salva"}</button>
          <button onClick={onCancel} disabled={saving} data-testid="listino-cancel"
                  style={btnSm("#757575")}>Annulla</button>
        </div>
      </td>
    </tr>
  );
}

function btnSm(bg) {
  return {
    background: bg, color: "white", border: "none",
    padding: "4px 10px", fontSize: 11, fontWeight: 700,
    cursor: "pointer", borderRadius: 2,
  };
}

const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, letterSpacing: 0.3 };
const thR = { ...th, textAlign: "right" };
const td = { padding: "6px 10px", fontSize: 12 };
const tdR = { ...td, textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 600 };
const inpEdit = {
  width: "100%", padding: "4px 6px", fontSize: 12,
  border: "1px solid #ffb300", borderRadius: 2, boxSizing: "border-box",
  background: "white", fontFamily: "Consolas, monospace",
};
